import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrivacyLinks from "@/components/PrivacyLinks";
import BonificoIstantaneoButton from "@/components/BonificoIstantaneoButton";
import CheckoutConsents from "@/components/CheckoutConsents";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { UserCheck, Briefcase, Building2, Check, ArrowRight, Shield, Tag, Video, MapPin, Loader2 } from "lucide-react";

// Mapping attività → pacchetto consulenza (per pre-selezione da /scopri-polizze)
const ATTIVITA_TO_PACCHETTO = {
  studente: "privati",
  dipendente: "privati",
  pensionato: "privati",
  casalingo_caregiver: "privati",
  libero_professionista: "professionisti",
  medico_sanitario: "professionisti",
  imprenditore: "aziende",
  commerciante: "aziende",
  agricoltore: "aziende",
};

// v1.70 (UX fix) — mapping prodotto → pacchetto quando l'utente arriva
// da una CTA "Prossimamente" o dalla card Viaggi (contatti?prodotto=...)
const PRODOTTO_TO_PACCHETTO = {
  // Aziende (CTA Prossimamente in AziendePage)
  rc_professionale: "aziende",
  rc_impresa:       "aziende",
  all_risks:        "aziende",
  // Tempo Libero (ConvenzioniPage)
  sport:            "privati",
  hobby:            "privati",
  animali:          "privati",
  // Viaggi (HomePage / FunnelPage)
  viaggio:          "privati",
};

const PRODOTTO_LABELS = {
  rc_professionale: "RC Professionale",
  rc_impresa:       "RC Operativa d'Impresa",
  all_risks:        "All Risks Property",
  sport:            "Sport & Attività",
  hobby:            "Hobby / Passioni",
  animali:          "Animali Domestici",
  viaggio:          "Assicurazione Viaggio",
};

const ATTIVITA_LABELS = {
  studente: "Studente",
  dipendente: "Dipendente",
  libero_professionista: "Libero professionista",
  medico_sanitario: "Medico / Sanitario",
  imprenditore: "Imprenditore",
  commerciante: "Commerciante / Artigiano",
  agricoltore: "Agricoltore",
  pensionato: "Pensionato",
  casalingo_caregiver: "Casalingo / Caregiver",
};

const CONSULENZE = [
  {
    id: "privati",
    nome: "Coperture Privati",
    prezzo: 100,
    icon: UserCheck,
    color: "border-[#163321]/30 bg-[#163321]/5",
    colorActive: "border-[#163321] bg-[#163321]/10 ring-2 ring-[#163321]/20",
    iconBg: "bg-[#163321] text-white",
    incluso: [
      "Analisi completa delle tue esigenze assicurative",
      "Verifica coperture esistenti e gap di protezione",
      "Proposta personalizzata Auto, Casa, Vita, Infortuni",
      "Confronto tra le migliori soluzioni sul mercato",
      "Report scritto con raccomandazioni",
    ],
  },
  {
    id: "professionisti",
    nome: "Coperture Professionisti",
    prezzo: 150,
    icon: Briefcase,
    color: "border-[#D97B58]/30 bg-[#D97B58]/5",
    colorActive: "border-[#D97B58] bg-[#D97B58]/10 ring-2 ring-[#D97B58]/20",
    iconBg: "bg-[#D97B58] text-white",
    popular: true,
    incluso: [
      "Tutto il pacchetto Privati incluso",
      "Analisi RC Professionale obbligatoria (Legge Gelli)",
      "Verifica adeguatezza massimali e retroattivita'",
      "Consulenza su Tutela Legale professionale",
      "Analisi rischi specifici della tua professione",
      "Piano di protezione integrato privato + professionale",
    ],
  },
  {
    id: "aziende",
    nome: "Coperture Aziende",
    prezzo: 200,
    icon: Building2,
    color: "border-[#163321]/30 bg-[#163321]/5",
    colorActive: "border-[#163321] bg-[#163321]/10 ring-2 ring-[#163321]/20",
    iconBg: "bg-[#163321] text-white",
    incluso: [
      "Tutto il pacchetto Professionisti incluso",
      "Audit completo rischi aziendali",
      "Analisi RC verso terzi, D&O, Cyber Risk",
      "Verifica coperture dipendenti e collaboratori",
      "Piano assicurativo aziendale integrato",
      "Consulenza su ottimizzazione fiscale premi",
      "Follow-up trimestrale primo anno incluso",
    ],
  },
];

function fmt(val) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(val);
}

export default function ConsulenzaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState("professionisti");
  const [modalita, setModalita] = useState("");
  const [codiceSconto, setCodiceSconto] = useState("");
  const [scontoApplicato, setScontoApplicato] = useState(null);
  const [verificandoSconto, setVerificandoSconto] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Pre-seleziona pacchetto in base all'attività passata da /scopri-polizze
  const fromAttivita = searchParams.get("attivita");
  const attivitaLabel = fromAttivita && fromAttivita !== "non_specificato" ? ATTIVITA_LABELS[fromAttivita] : null;
  // v1.70 — pre-selezione anche in base al prodotto (CTA Prossimamente + Tempo Libero + Viaggi)
  const fromProdotto = searchParams.get("prodotto");
  const prodottoLabel = fromProdotto ? (PRODOTTO_LABELS[fromProdotto] || fromProdotto) : null;
  useEffect(() => {
    if (fromAttivita && ATTIVITA_TO_PACCHETTO[fromAttivita]) {
      setSelected(ATTIVITA_TO_PACCHETTO[fromAttivita]);
    } else if (fromProdotto && PRODOTTO_TO_PACCHETTO[fromProdotto]) {
      setSelected(PRODOTTO_TO_PACCHETTO[fromProdotto]);
    }
  }, [fromAttivita, fromProdotto]);
  const [consents, setConsents] = useState({ termini: false, clausole: false, recesso: false });
  const consentsOk = !!(consents.termini && consents.clausole && consents.recesso);

  const consulenza = CONSULENZE.find(c => c.id === selected);
  const API = process.env.REACT_APP_BACKEND_URL;

  const verificaSconto = async () => {
    if (!codiceSconto.trim()) return;
    setVerificandoSconto(true);
    try {
      const resp = await fetch(`${API}/api/codici-sconto/verifica`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codice: codiceSconto.trim().toUpperCase(), tipo_consulenza: selected }),
      });
      const data = await resp.json();
      if (data.valido) {
        setScontoApplicato(data);
      } else {
        setScontoApplicato({ valido: false, messaggio: data.messaggio || "Codice non valido" });
      }
    } catch {
      setScontoApplicato({ valido: false, messaggio: "Errore nella verifica. Riprova." });
    }
    setVerificandoSconto(false);
  };

  const prezzoFinale = scontoApplicato?.valido
    ? Math.max(0, consulenza.prezzo - (scontoApplicato.sconto_euro || 0))
    : consulenza.prezzo;

  const handlePagamento = async () => {
    if (!modalita || processing) return;
    setProcessing(true);
    try {
      const resp = await fetch(`${API}/api/pagamenti/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          package_id: selected,
          origin_url: window.location.origin,
          modalita,
          codice_sconto: scontoApplicato?.valido ? scontoApplicato.codice : "",
          attivita: fromAttivita || "non_specificato",
          eta: searchParams.get("eta") || null,
          livello_copertura: searchParams.get("livello") || null,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || "Errore nel pagamento");
      }
      const data = await resp.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      alert(err.message || "Errore nel processo di pagamento. Riprova.");
      setProcessing(false);
    }
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FCFCFA] pt-20" data-testid="consulenza-page">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 py-12">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-14 h-14 mx-auto mb-4 bg-[#163321]/10 rounded-full flex items-center justify-center">
              <Shield className="w-7 h-7 text-[#163321]" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-[#0F1F14] mb-2" data-testid="consulenza-title">
              Prenota la tua Consulenza
            </h1>
            <p className="text-sm text-[#4A5E51] max-w-md mx-auto">
              Un consulente dedicato analizza le tue esigenze e costruisce la protezione su misura per te.
            </p>
          </div>

          {/* Banner profilo da /scopri-polizze */}
          {attivitaLabel && (
            <div className="mb-6 p-4 bg-gradient-to-r from-[#163321]/5 to-[#D97B58]/5 border border-[#163321]/20 rounded-xl flex items-center gap-3" data-testid="consulenza-profilo-banner">
              <div className="w-10 h-10 rounded-full bg-[#163321] text-white flex items-center justify-center shrink-0">
                <Check className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-[#D97B58] font-semibold">Profilo rilevato</p>
                <p className="text-sm text-[#0F1F14] font-medium">
                  <strong>{attivitaLabel}</strong> — abbiamo pre-selezionato il pacchetto più adatto al tuo profilo professionale
                </p>
              </div>
            </div>
          )}
          {/* v1.70 — banner prodotto: quando arriva da CTA Prossimamente / Tempo Libero / Viaggi */}
          {prodottoLabel && !attivitaLabel && (
            <div className="mb-6 p-4 rounded-xl bg-[#163321]/5 border border-[#163321]/20 flex items-start gap-3"
                  data-testid="consulenza-prodotto-banner">
              <div className="w-9 h-9 rounded-full bg-[#163321] text-white flex items-center justify-center shrink-0">
                <Check className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-[#163321] font-semibold">Prodotto richiesto</p>
                <p className="text-sm text-[#0F1F14] font-medium">
                  <strong>{prodottoLabel}</strong> — abbiamo pre-selezionato il pacchetto giusto. Contatta lo studio per un preventivo su misura.
                </p>
              </div>
            </div>
          )}

          {/* Fasce di prezzo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="consulenza-cards">
            {CONSULENZE.map(c => {
              const Icon = c.icon;
              const isSelected = selected === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => { setSelected(c.id); setScontoApplicato(null); }}
                  className={`relative text-left p-6 rounded-2xl border-2 transition-all ${isSelected ? c.colorActive : c.color} hover:shadow-md`}
                  data-testid={`consulenza-card-${c.id}`}
                >
                  {c.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D97B58] text-white text-[9px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                      Consigliato
                    </span>
                  )}
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${c.iconBg}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading text-base font-medium text-[#0F1F14] mb-1">{c.nome}</h3>
                  <p className="text-2xl font-heading font-medium text-[#163321] mb-3">{fmt(c.prezzo)}</p>
                  <ul className="space-y-2">
                    {c.incluso.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-[10px] text-[#4A5E51]">
                        <Check className="w-3 h-3 text-[#163321] shrink-0 mt-0.5" /><span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {isSelected && (
                    <div className="absolute top-4 right-4 w-5 h-5 rounded-full bg-[#163321] flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Configurazione */}
          <div className="bg-white rounded-2xl border border-[#E5E2DB] p-6 sm:p-8 shadow-sm space-y-6 mb-8" data-testid="consulenza-config">
            <h2 className="font-heading text-lg font-medium text-[#0F1F14]">Configura la tua consulenza</h2>

            <div className="space-y-3">
              <Label className="text-sm font-medium text-[#0F1F14]">Come preferisci incontrare il consulente?</Label>
              <RadioGroup value={modalita} onValueChange={setModalita} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${modalita === "presenza" ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] hover:border-[#163321]/30"}`}>
                  <RadioGroupItem value="presenza" id="mod-presenza" data-testid="consulenza-presenza" />
                  <MapPin className="w-5 h-5 text-[#163321]" />
                  <div>
                    <p className="text-sm font-medium text-[#0F1F14]">Di persona</p>
                    <p className="text-[10px] text-[#4A5E51]">Presso i nostri uffici a Napoli</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${modalita === "videocall" ? "border-[#163321] bg-[#163321]/5" : "border-[#E5E2DB] hover:border-[#163321]/30"}`}>
                  <RadioGroupItem value="videocall" id="mod-video" data-testid="consulenza-videocall" />
                  <Video className="w-5 h-5 text-[#163321]" />
                  <div>
                    <p className="text-sm font-medium text-[#0F1F14]">Video call</p>
                    <p className="text-[10px] text-[#4A5E51]">Comodo da casa o dall'ufficio</p>
                  </div>
                </label>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#0F1F14]">Hai un codice sconto?</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5E51]" />
                  <Input
                    value={codiceSconto}
                    onChange={e => { setCodiceSconto(e.target.value.toUpperCase()); setScontoApplicato(null); }}
                    placeholder="es. BENVENUTO20"
                    className="rounded-xl border-[#E5E2DB] h-11 pl-10 uppercase"
                    data-testid="consulenza-codice-input"
                  />
                </div>
                <Button onClick={verificaSconto} disabled={!codiceSconto.trim() || verificandoSconto} variant="outline"
                  className="rounded-xl h-11 px-5 border-[#163321] text-[#163321] hover:bg-[#163321]/5 text-sm" data-testid="consulenza-verifica-btn">
                  {verificandoSconto ? "..." : "Applica"}
                </Button>
              </div>
              {scontoApplicato && (
                <p className={`text-xs ${scontoApplicato.valido ? "text-green-600" : "text-red-500"}`} data-testid="consulenza-sconto-msg">
                  {scontoApplicato.valido ? `Sconto di ${fmt(scontoApplicato.sconto_euro)} applicato!` : scontoApplicato.messaggio}
                </p>
              )}
            </div>
          </div>

          {/* Riepilogo */}
          <div className="bg-gradient-to-br from-[#163321] to-[#224A32] rounded-2xl p-6 sm:p-8 text-white mb-8" data-testid="consulenza-riepilogo">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Riepilogo</p>
                <p className="text-lg font-heading font-medium">{consulenza.nome}</p>
                {modalita && (
                  <p className="text-xs text-white/60 mt-1">{modalita === "presenza" ? "Di persona a Napoli" : "Video call"}</p>
                )}
              </div>
              <div className="text-center sm:text-right">
                {scontoApplicato?.valido && (
                  <p className="text-sm text-white/40 line-through">{fmt(consulenza.prezzo)}</p>
                )}
                <p className="text-3xl font-heading font-medium" data-testid="consulenza-prezzo-finale">{fmt(prezzoFinale)}</p>
                <p className="text-[10px] text-white/50">IVA inclusa</p>
              </div>
            </div>
            <div className="mt-6">
              {modalita && (
                <div className="mb-4">
                  <CheckoutConsents values={consents} onChange={setConsents} />
                </div>
              )}
              <Button
                disabled={!modalita || !consentsOk || processing}
                className="w-full rounded-xl h-12 bg-[#D97B58] text-white hover:bg-[#C06A48] text-sm font-medium disabled:opacity-50"
                data-testid="consulenza-prenota-btn"
                onClick={handlePagamento}
              >
                {processing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reindirizzamento al pagamento...</>
                ) : !modalita ? (
                  "Seleziona la modalita'"
                ) : !consentsOk ? (
                  "Accetta termini e condizioni per procedere"
                ) : (
                  <>{`Paga ${fmt(prezzoFinale)} e Prenota`} <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
              <p className="text-[10px] text-white/40 text-center mt-3">
                Pagamento sicuro con Stripe. Dopo il pagamento sceglierai data e ora della consulenza.
              </p>

              {/* ALTERNATIVA BONIFICO ISTANTANEO */}
              {modalita && consentsOk && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-[10px] uppercase tracking-wider text-white/40 text-center mb-2 font-semibold">
                    Oppure, azzera le commissioni
                  </p>
                  <BonificoIstantaneoButton
                    importo={prezzoFinale}
                    causale={`Consulenza ${CONSULENZE.find(c => c.id === selected)?.nome || ""} - ${modalita === "presenza" ? "Di persona" : "Video call"} - ${new Date().toLocaleDateString("it-IT")}`}
                    variant="secondary"
                    className="w-full"
                    label={`Paga ${fmt(prezzoFinale)} con Bonifico Istantaneo`}
                    testId="consulenza-bonifico-btn"
                  />
                  <p className="text-[10px] text-white/40 text-center mt-2">
                    Accredito in 10 secondi. Ti contatteremo per fissare l'appuntamento.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Garanzie */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { title: "Consulente dedicato", desc: "Un professionista esperto del settore ti segue passo dopo passo" },
              { title: "Senza impegno", desc: "Nessun obbligo di sottoscrivere polizze dopo la consulenza" },
            ].map((g, i) => (
              <div key={i} className="p-4 bg-[#F5F4F0] rounded-xl text-center">
                <p className="text-sm font-medium text-[#0F1F14] mb-1">{g.title}</p>
                <p className="text-[10px] text-[#4A5E51]">{g.desc}</p>
              </div>
            ))}
          </div>
          <PrivacyLinks className="mt-8" />
        </div>
      </main>
      <Footer />
    </>
  );
}
